import { AnyObject } from 'final-form';
import { HorizonSummary } from './tractHorizonSummary';
import ReportHorizonDto from './reports/ReportHorizonDto';

export class Formation {
    id: number;
    name: string;

    static from(object: AnyObject) {
        return Object.assign(new Formation(), object);
    }
}

export class NamedHorizon {
    isAbove: boolean;
    isBelow: boolean;
    isTop: boolean;
    isBase: boolean;
    depth: number;
    formation: Formation;
    approximateDepth: number;

    static from = (object: AnyObject) => {
        const namedHorizon = Object.assign(new NamedHorizon(), object);

        if (namedHorizon.topBase) {
            namedHorizon.isTop = (namedHorizon.topBase as string).toLowerCase() === 'top' ? true : false;
            namedHorizon.isBase = (namedHorizon.topBase as string).toLowerCase() === 'base' ? true : false;
        }

        if (namedHorizon.aboveBelow) {
            namedHorizon.isAbove = (namedHorizon.aboveBelow as string).toLowerCase() === 'above' ? true : false;
            namedHorizon.isBelow = (namedHorizon.aboveBelow as string).toLowerCase() === 'below' ? true : false;
        }

        if (namedHorizon && namedHorizon.formation) {
            namedHorizon.formation = Formation.from(namedHorizon.formation);
        }

        return namedHorizon;
    };

    toString = () => {
        let retString = '';

        if (!this.formation) {
            return '[incomplete definition]';
        }

        if (this.depth && (this.isAbove || this.isBelow)) {
            retString = `${this.depth} feet ${this.isAbove ? 'above' : 'below'} `;
        }

        if (this.isBase || this.isTop) {
            retString += `the ${this.isBase ? 'Base' : 'Top'} of `;
        }

        retString += 'the ' + this.formation.name + ' formation';

        if (this.approximateDepth) {
            retString += ` (${this.approximateDepth} feet)`;
        }

        return retString;
    };
}

export class HorizonPart {
    type: 'extent' | 'formation' | 'depth' | 'custom';
    isSurface: boolean;
    isCenterEarth: boolean;
    namedHorizon: NamedHorizon;
    depth: number;
    custom: string;

    static from = (object: AnyObject) => {
        const part = Object.assign(new HorizonPart(), object);

        if (object && object.namedHorizon) {
            part.namedHorizon = NamedHorizon.from(object.namedHorizon);
        }

        if (object?.type !== undefined) {
            switch (object.type) {
                case 0:
                    part.type = 'extent';
                    break;
                case 1:
                    part.type = 'formation';
                    break;
                case 2:
                    part.type = 'depth';
                    break;
                case 3:
                    part.type = 'custom';
                    break;
                default:
                    break;
            }
        }

        return part;
    };

    calcType = () => {
        if (this.isSurface || this.isCenterEarth) {
            this.type = 'extent';
        } else if (this.depth !== null) {
            this.type = 'depth';
        } else if (this.custom !== null) {
            this.type = 'custom';
        } else {
            this.type = 'formation';
        }
    };

    toString = () => {
        switch (this.type) {
            case 'extent':
                if (this.isSurface) {
                    return 'Surface';
                } else {
                    return 'Center of Earth';
                }
            case 'custom':
                return this.custom ? this.custom : '[incomplete definition]';
            case 'depth':
                return this.depth ? `${this.depth} feet` : '[incomplete definition]';
            case 'formation':
                return NamedHorizon.from(this.namedHorizon).toString();
            default:
                return '';
        }
    };
}

export class HorizonInterval {
    id: number;
    horizonId: number;
    horizonNumber: number;
    description: string;
    isTargetHorizon: boolean;
    sequencePosition: number;
    from: HorizonPart;
    to: HorizonPart;

    // Not Mapped
    sectionNumber: number;

    static from = (object: AnyObject): HorizonInterval => {
        const interval = Object.assign(new HorizonInterval(), object);

        if (object && object.from) {
            interval.from = HorizonPart.from(object.from);
        }

        if (object && object.to) {
            interval.to = HorizonPart.from(object.to);
        }

        return interval;
    };

    static fromSummary = (object: HorizonSummary | ReportHorizonDto): HorizonInterval => {
        const interval = Object.assign(new HorizonInterval(), object);

        interval.id = object.horizonIntervalId;

        if (object && object.from) {
            interval.from = HorizonPart.from(object.from);
        }

        if (object && object.to) {
            interval.to = HorizonPart.from(object.to);
        }

        return interval;
    }

    toString = (): string => {
        if (!this.from || !this.to) {
            return '';
        }
        return `From ${HorizonPart.from(this.from).toString()} To ${HorizonPart.from(this.to).toString()}`;
    };
}



// WEBPACK FOOTER //
// ./src/models/Horizon.ts